import { SkeletonLoader } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LeadV2Status } from "../api/generated";
import { LeftNotch, LightBulbIcon, RightPointer } from "../Icons";
import { Typo } from "../Typo";

// ─── Types ────────────────────────────────────────────────────────────────────

export type FunnelTabId =
  | "all-leads"
  | "live-account-setup"
  | "e-and-i"
  | "experience-check"
  | "identity-verification"
  | "under-review"
  | "approved";

type TabConfig = {
  id: FunnelTabId;
  label: string;
  count?: number;
};

type FunnelTabPosition = "first" | "middle" | "last";

// ─── Tab → Status mapping ─────────────────────────────────────────────────────

export const TAB_STATUS_MAP: Record<FunnelTabId, LeadV2Status[] | null> = {
  "all-leads": null,
  "live-account-setup": [LeadV2Status.IncompleteProfile],
  "e-and-i": [LeadV2Status.CompletingProfile],
  "experience-check": [LeadV2Status.ReviewingSuitability],
  "identity-verification": [LeadV2Status.IdentifyVerification],
  "under-review": [LeadV2Status.VerifyingIdentity],
  approved: [LeadV2Status.Approved],
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function TabContent({
  label,
  count,
  isActive,
  isLoading,
}: {
  label: string;
  count: number | undefined;
  isActive: boolean;
  isLoading?: boolean;
}) {
  const showSkeleton = isLoading || count === undefined;

  return (
    <div className="flex h-full w-full flex-col items-start justify-between">
      {showSkeleton ? (
        <SkeletonLoader variant="rectangular" height={6} width={24} />
      ) : (
        <Typo
          type="labelSM"
          smType="labelXS"
          textColor={isActive ? "text-white/90" : "text-text-primary/70"}
          classes="text-left"
        >
          {label}
        </Typo>
      )}
      {showSkeleton ? (
        <SkeletonLoader variant="rectangular" height={6} width={16} />
      ) : (
        <Typo
          type="headingXL"
          smType="headingMD"
          textColor={isActive ? "text-white" : "text-text-primary"}
          classes="whitespace-nowrap"
        >
          {count}
        </Typo>
      )}
    </div>
  );
}

function FunnelTab({
  tab,
  isActive,
  position,
  onClick,
  isLoading,
}: {
  tab: TabConfig;
  isActive: boolean;
  position: FunnelTabPosition;
  onClick: () => void;
  isLoading?: boolean;
}) {
  const isFirst = position === "first";
  const isLast = position === "last";
  const isActiveStyle = isActive && !isLoading;
  const bgClass = isActiveStyle
    ? "bg-surface-emphasis"
    : "bg-surface-foreground-brand";
  const hoverBg = !isActiveStyle ? "group-hover:bg-[rgb(233,238,245)]" : "";
  const fillClass = isActiveStyle
    ? "[&_path]:fill-surface-emphasis"
    : "[&_path]:fill-surface-foreground-brand group-hover:[&_path]:fill-[rgb(233,238,245)]";

  return (
    <button
      type="button"
      className={[
        "group relative flex h-[96px] shrink-0 cursor-pointer items-center lg:h-[127px] lg:min-w-0 lg:flex-1 lg:basis-0",
        !isLast ? "isolate ltr:pr-4 rtl:pl-4" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={onClick}
    >
      {!isFirst && (
        <div className="relative z-[3] h-[96px] w-[39px] shrink-0 lg:h-[127px] ltr:-mr-4 rtl:-ml-4">
          <LeftNotch
            className={clsx(["block size-full rtl:scale-x-[-1]", fillClass])}
          />
        </div>
      )}
      <div
        className={[
          "relative flex h-[96px] w-[108px] flex-col items-start justify-between lg:h-[127px]",
          bgClass,
          hoverBg,
          isLast
            ? "p-4 lg:w-full lg:min-w-0 lg:flex-1 lg:p-6"
            : "z-[2] p-4 lg:px-4 lg:py-6 ltr:-mr-4 ltr:pl-4 rtl:-ml-4 rtl:pr-4",
          isFirst
            ? "min-w-0 flex-[1_0_0]"
            : !isLast
              ? "min-w-0 flex-1 lg:w-auto"
              : "",
          isFirst
            ? "ltr:rounded-l-lg rtl:rounded-r-lg"
            : isLast
              ? "ltr:rounded-r-lg rtl:rounded-l-lg"
              : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <TabContent
          label={tab.label}
          count={tab.count}
          isActive={isActive}
          isLoading={isLoading}
        />
      </div>
      {!isLast && (
        <div className="relative z-[1] h-[96px] w-[44px] shrink-0 lg:h-[127px] ltr:-mr-4 rtl:-ml-4">
          <RightPointer
            className={clsx(["block size-full rtl:scale-x-[-1]", fillClass])}
          />
        </div>
      )}
    </button>
  );
}

function CardTab({
  tab,
  isActive,
  onClick,
  isLoading,
}: {
  tab: TabConfig;
  isActive: boolean;
  onClick: () => void;
  isLoading?: boolean;
}) {
  const bgClass =
    isActive && !isLoading
      ? "bg-surface-emphasis"
      : "bg-surface-foreground-brand";
  const hoverClass = !(isActive && !isLoading)
    ? "hover:bg-[rgb(233,238,245)]"
    : "";

  return (
    <button
      type="button"
      className={clsx([
        "relative flex h-[96px] w-[136px] shrink-0 cursor-pointer flex-col items-start rounded-lg p-4",
        "lg:h-[127px] lg:w-auto lg:flex-1 lg:basis-0 lg:p-6",
        bgClass,
        hoverClass,
      ])}
      onClick={onClick}
    >
      <div className="h-full w-full">
        <TabContent
          label={tab.label}
          count={tab.count}
          isActive={isActive}
          isLoading={isLoading}
        />
      </div>
    </button>
  );
}

function Divider() {
  return <div className="h-[96px] w-px bg-text-primary/10 lg:h-[127px]" />;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export type FunnelTabsProps = {
  onTabChange?: (tabId: FunnelTabId) => void;
  statusCounts?: Partial<Record<FunnelTabId, number>>;
  isLoading?: boolean;
  onLearnMoreClick?: () => void;
};

// ─── Main Component ───────────────────────────────────────────────────────────

export function FunnelTabs({
  onTabChange,
  statusCounts,
  isLoading,
  onLearnMoreClick,
}: FunnelTabsProps) {
  const [activeTab, setActiveTab] = useState<FunnelTabId>("all-leads");
  const { t } = useTranslation();

  const handleTabClick = (tabId: FunnelTabId) => {
    setActiveTab(tabId);
    onTabChange?.(tabId);
  };

  const allLeadsTab: TabConfig = {
    id: "all-leads",
    label: t("leadsPage_table_status_all_leads"),
    count: statusCounts?.["all-leads"],
  };

  const approvedTab: TabConfig = {
    id: "approved",
    label: t("leadsPage_table_status_approved"),
    count: statusCounts?.["approved"],
  };

  const funnelTabs: TabConfig[] = (
    [
      {
        id: "live-account-setup",
        label: t("leadsPage_table_status_demo_label"),
      },
      { id: "e-and-i", label: t("leadsPage_table_status_lead_1_label") },
      {
        id: "experience-check",
        label: t("leadsPage_table_status_lead_2_label"),
      },
      {
        id: "identity-verification",
        label: t("leadsPage_table_status_lead_4_label"),
      },
      { id: "under-review", label: t("leadsPage_table_status_lead_5_label") },
    ] as TabConfig[]
  ).map((tab) => ({ ...tab, count: statusCounts?.[tab.id] }));

  return (
    <div className="flex w-full flex-col items-end justify-center gap-4 lg:gap-6">
      <div id="funnel-tabs-section" className="w-full overflow-x-auto">
        <div className="flex w-max items-center gap-0 lg:grid lg:w-full lg:grid-cols-[1fr_auto_repeat(5,_1fr)_auto_1fr]">
          <CardTab
            tab={allLeadsTab}
            isActive={activeTab === "all-leads"}
            isLoading={isLoading}
            onClick={() => handleTabClick("all-leads")}
          />
          <div className="mx-4 shrink-0 lg:mx-6">
            <Divider />
          </div>
          {funnelTabs.map((tab, index) => (
            <FunnelTab
              key={tab.id}
              tab={tab}
              isActive={activeTab === tab.id}
              isLoading={isLoading}
              position={
                index === 0
                  ? "first"
                  : index === funnelTabs.length - 1
                    ? "last"
                    : "middle"
              }
              onClick={() => handleTabClick(tab.id)}
            />
          ))}
          <div className="mx-4 shrink-0 lg:mx-6">
            <Divider />
          </div>
          <CardTab
            tab={approvedTab}
            isActive={activeTab === "approved"}
            isLoading={isLoading}
            onClick={() => handleTabClick("approved")}
          />
        </div>
      </div>

      <div
        id="funnel-learn-more"
        className="flex w-full shrink-0 items-baseline justify-start gap-1 lg:w-auto lg:justify-end"
      >
        {isLoading ? (
          <SkeletonLoader variant="rectangular" height={5} width={80} />
        ) : (
          <>
            <LightBulbIcon className="size-[10px] shrink-0 translate-y-[1px] text-text-brand-default" />
            <button
              type="button"
              onClick={onLearnMoreClick}
              className="cursor-pointer border-none bg-transparent p-0 text-left underline decoration-text-brand-default [text-decoration-skip-ink:none]"
            >
              <Typo type="captionXS" textColor="text-text-brand-default">
                {t("leadsPage_funnel_learn_more")}
              </Typo>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
